import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { GestorePageHeader } from '../../components/gestore/GestorePageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Search, Trash2, Mail, Phone, MapPin, Building2, Inbox, Store } from 'lucide-react';

const STATUSES = ['new', 'contacted', 'converted', 'discarded'];
const STATUS_STYLES = {
  new: 'bg-primary/10 text-primary',
  contacted: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  converted: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  discarded: 'bg-muted text-muted-foreground',
};

export default function GestoreLeads() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [confirm, setConfirm] = useState(null);

  const statusLabel = (s) => t(`gestore.leadStatus.${s}`, {
    defaultValue: { new: 'Nuova', contacted: 'Contattato', converted: 'Convertito', discarded: 'Scartato' }[s] || s,
  });
  const typeLabel = (v) => t(`landing.form.type${{ ristorante: 'Restaurant', bar: 'Bar', pizzeria: 'Pizzeria', caffetteria: 'Cafe', pub: 'Pub', negozio: 'Store', altro: 'Other' }[v] || 'Other'}`, {
    defaultValue: { ristorante: 'Ristorante', bar: 'Bar', pizzeria: 'Pizzeria', caffetteria: 'Caffetteria', pub: 'Pub', negozio: 'Negozio / Retail', altro: 'Altro' }[v] || v,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get('/leads');
      setItems(data || []);
    } catch (e) {
      toast.error(t('gestore.leadLoadError', { defaultValue: 'Errore nel caricamento delle richieste' }));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (lead, status) => {
    const prev = items;
    setItems((arr) => arr.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    try {
      await adminApi.patch(`/leads/${lead.id}`, { status });
      toast.success(t('gestore.leadStatusUpdated', { defaultValue: 'Stato aggiornato' }));
    } catch (e) {
      setItems(prev);
      toast.error(t('gestore.leadUpdateError', { defaultValue: 'Aggiornamento non riuscito' }));
    }
  };

  const doDelete = async (lead) => {
    try {
      await adminApi.delete(`/leads/${lead.id}`);
      setItems((arr) => arr.filter((l) => l.id !== lead.id));
      toast.success(t('gestore.leadDeleted', { defaultValue: 'Richiesta eliminata' }));
    } catch (e) {
      toast.error(t('gestore.leadDeleteError', { defaultValue: 'Eliminazione non riuscita' }));
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((l) => {
      if (filter !== 'all' && l.status !== filter) return false;
      if (!term) return true;
      return [l.first_name, l.last_name, l.email, l.business_name, l.business_address, l.phone]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(term));
    });
  }, [items, q, filter]);

  const newCount = items.filter((l) => l.status === 'new').length;
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(i18n.language); } catch { return iso || ''; } };

  const StatusBadge = ({ s }) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[s] || 'bg-muted text-muted-foreground'}`}>
      {statusLabel(s)}
    </span>
  );

  return (
    <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6" data-testid="gestore-leads-page">
      <GestorePageHeader
        title={t('gestore.leadsTitle', { defaultValue: 'Richieste di contatto' })}
        subtitle={t('gestore.leadsSubtitle', { total: items.length, neww: newCount, defaultValue: `${items.length} richieste · ${newCount} nuove` })}
      />

      {/* Filtri */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('gestore.leadSearch', { defaultValue: 'Cerca per nome, email, attività…' })} className="pl-9" data-testid="leads-search-input" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-52" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('gestore.leadFilterAll', { defaultValue: 'Tutti gli stati' })}</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">{t('common.loading', { defaultValue: 'Caricamento…' })}</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="leads-empty">
          <Inbox className="h-10 w-10 mx-auto text-muted-foreground/60" />
          <p className="mt-3 text-muted-foreground">{t('gestore.leadsEmpty', { defaultValue: 'Nessuna richiesta al momento.' })}</p>
        </Card>
      ) : (<>
        {/* Desktop table */}
        <Card className="p-0 hidden lg:block overflow-hidden">
          <Table data-testid="gestore-leads-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('gestore.leadColContact', { defaultValue: 'Contatto' })}</TableHead>
                <TableHead>{t('gestore.leadColBusiness', { defaultValue: 'Attività' })}</TableHead>
                <TableHead>{t('gestore.leadColDate', { defaultValue: 'Ricevuta' })}</TableHead>
                <TableHead>{t('gestore.leadColStatus', { defaultValue: 'Stato' })}</TableHead>
                <TableHead className="text-right">{t('gestore.leadColActions', { defaultValue: 'Azioni' })}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                  <TableCell>
                    <div className="font-medium">{l.first_name} {l.last_name}</div>
                    <a href={`mailto:${l.email}`} className="text-xs text-primary hover:underline flex items-center gap-1"><Mail className="h-3 w-3" /> {l.email}</a>
                    {l.phone && <a href={`tel:${l.phone}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><Phone className="h-3 w-3" /> {l.phone}</a>}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {l.business_name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Store className="h-3 w-3" /> {typeLabel(l.business_type)}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {l.business_address}</div>
                    {l.note && <div className="text-xs text-muted-foreground italic mt-0.5">“{l.note}”</div>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                  <TableCell><StatusBadge s={l.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Select value={l.status} onValueChange={(v) => setStatus(l, v)}>
                        <SelectTrigger className="w-36 h-9" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" className="text-destructive" data-testid={`lead-delete-${l.id}`} onClick={() => setConfirm(l)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Mobile / tablet card list */}
        <div className="lg:hidden flex flex-col gap-3" data-testid="gestore-leads-card-list">
          {filtered.map((l) => (
            <Card key={l.id} className="p-4" data-testid={`lead-card-${l.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium">{l.first_name} {l.last_name}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(l.created_at)}</div>
                </div>
                <StatusBadge s={l.status} />
              </div>
              <div className="mt-2 space-y-1 text-sm">
                <a href={`mailto:${l.email}`} className="text-primary hover:underline flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {l.email}</a>
                {l.phone && <a href={`tel:${l.phone}`} className="text-muted-foreground flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {l.phone}</a>}
                <div className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" /> {l.business_name} · <span className="text-muted-foreground">{typeLabel(l.business_type)}</span></div>
                <div className="text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {l.business_address}</div>
                {l.note && <div className="text-muted-foreground italic">“{l.note}”</div>}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Select value={l.status} onValueChange={(v) => setStatus(l, v)}>
                  <SelectTrigger className="flex-1 h-9" data-testid={`lead-status-mobile-${l.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="text-destructive" data-testid={`lead-delete-mobile-${l.id}`} onClick={() => setConfirm(l)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </>)}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gestore.leadDeleteTitle', { defaultValue: 'Eliminare la richiesta?' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('gestore.leadDeleteDesc', { name: confirm ? `${confirm.first_name} ${confirm.last_name}` : '', defaultValue: 'Questa operazione è irreversibile.' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-delete-cancel">{t('gestore.lCancel', { defaultValue: 'Annulla' })}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const c = confirm; setConfirm(null); if (c) doDelete(c); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="lead-delete-confirm">
              {t('gestore.lConfirm', { defaultValue: 'Conferma' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
